import * as THREE from "../../../libs/three.js/build/three.module.js";
import {BoxVolume} from "../../utils/Volume.js";
import {t} from "../../digitization/i18n/index.js";
import {LineSegments2} from "../../../libs/three.js/lines/LineSegments2.js";
import {LineSegmentsGeometry} from "../../../libs/three.js/lines/LineSegmentsGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {normalizeSnitplan, snitplanBox, snitplanFromBasis, SnitDisplayMode, SNIT_LIMITS} from "./snitplanGeometry.js";

// Snitplanets Potree-adapter: en BoxVolume (Potrees egen clip-boks) hvis position/rotation/skala UDELUKKENDE afledes af den fælles
// Snitplan-definition (snitplanGeometry.js) - aldrig omvendt. Clip-kæden (viewer.update -> PointCloudMaterial.setClipBoxes ->
// PotreeRenderer.renderNodes -> composeClipBoxLocalMatrices i float64, CPU-nodebeskæring ved SHOW_INSIDE) er derfor uændret.
//
//   - samme 3D-håndtag som en Clip-boks (flyt, drej frit, størrelse; TransformationTool) når Snittet er valgt. Håndtagene ændrer
//     boksens transform; den fælles definition afledes straks af den (_syncFromTransform), så talfelterne følger med. Al anden
//     geometri ændres via setDefinition() (popupen)
//   - ingen udfyldt boks og ingen volumen-label (kun en tynd ramme); ikke valgbar ved klik i 3D (valg sker fra træet)
//   - `clip` styres af SnitplanManager ud fra visningstilstanden (Normal = ikke i clip-puljen)
export class SnitVolume extends BoxVolume{

	constructor(definition = {}){
		super({});

		this.pvSnit = true;
		this._modifiable = true;
		// nedre grænse for håndtagenes størrelsesændring pr. akse (længde, højde, tykkelse) - TransformationTool
		this.minScale = [SNIT_LIMITS.minLength, SNIT_LIMITS.minHeight, SNIT_LIMITS.minThickness];
		this._displayMode = SnitDisplayMode.NORMAL;
		this.snit = normalizeSnitplan(definition);
		this.name = this.snit.name || t("Snit");
		this._createEdges();

		this._applyGeometry();

		const fromHandles = () => this._syncFromTransform();
		this.addEventListener("position_changed", fromHandles);
		this.addEventListener("orientation_changed", fromHandles);
		this.addEventListener("scale_changed", fromHandles);
	}

	// Snitplanets egen kantstyling (kun SnitVolume; BoxVolume/Clip/IFC/målinger beholder deres egen ramme): gule kanter, ca. dobbelt så
	// tykke som WebGL's 1 px-linjer, med en tynd mørk kant uden om, så de står tydeligt både mod mørke og lyse overflader. Tegnes som
	// skærm-rum-linjer (LineSegments2 + LineMaterial; bredden er i pixels og uafhængig af Snitplanets skala/UTM-position). Den
	// almindelige 1 px-ramme fra BoxVolume skjules.
	_createEdges(){
		this.frame.visible = false;

		const c = [-0.5, 0.5];
		const positions = [];
		const edge = (a, b) => positions.push(...a, ...b);
		for(const y of c) for(const z of c) edge([-0.5, y, z], [0.5, y, z]);
		for(const x of c) for(const z of c) edge([x, -0.5, z], [x, 0.5, z]);
		for(const x of c) for(const y of c) edge([x, y, -0.5], [x, y, 0.5]);
		const geometry = new LineSegmentsGeometry();
		geometry.setPositions(positions);

		const make = (color, linewidth, order) => {
			const material = new LineMaterial({color, linewidth, resolution: new THREE.Vector2(1000, 1000)});
			const lines = new LineSegments2(geometry, material);
			lines.renderOrder = order;
			lines.frustumCulled = false;
			// bredden er i pixels: opløsningen følger den faktiske rendering (uden afhængighed af viewer)
			const size = new THREE.Vector2();
			lines.onBeforeRender = (renderer) => {
				renderer.getSize(size);
				material.resolution.set(size.x, size.y);
			};
			this.add(lines);
			return lines;
		};
		this._edgeCasing = make(0x1a1a1a, 3.6, 1);
		this._edgeCore = make(0xFFD600, 2, 2);
	}

	// kopi af den fælles definition (rene tal)
	get definition(){
		return Object.assign({}, this.snit, {center: this.snit.center.slice()});
	}

	get displayMode(){
		return this._displayMode;
	}

	set displayMode(mode){
		this._displayMode = Object.values(SnitDisplayMode).includes(mode) ? mode : SnitDisplayMode.NORMAL;
	}

	// Ændrer definitionen (delvis) og afleder boksen igen. Returnerer den nye definition.
	setDefinition(patch){
		this.snit = normalizeSnitplan(patch, this.snit);
		this.name = this.snit.name || this.name;
		this._applyGeometry();
		this.dispatchEvent({type: "snit_changed", object: this});
		return this.definition;
	}

	// Definitionen afledt af boksens egen transform (efter at 3D-håndtagene har flyttet/drejet/skaleret den). Geometrien genanvendes IKKE
	// (ingen kanonisering midt i et træk - håndtagene ville ellers hoppe); næste setDefinition() lægger den kanoniske form.
	_syncFromTransform(){
		const patch = snitDefinitionFromBox(this, this.snit.azimuth);
		this.snit = normalizeSnitplan(patch, this.snit);
		this.dispatchEvent({type: "snit_changed", object: this});
	}

	_applyGeometry(){
		const {center, scale, axes} = snitplanBox(this.snit);
		this.position.set(center[0], center[1], center[2]);
		this.scale.set(scale[0], scale[1], scale[2]);
		this.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(
			new THREE.Vector3(...axes.s), new THREE.Vector3(...axes.u), new THREE.Vector3(...axes.n)));
		this.updateMatrix();
		this.updateMatrixWorld(true);
	}

	update(){
		super.update();
		// BoxVolume.update() viser den udfyldte boks/label, når volumenen ikke er clip - et Snitplan viser kun sin ramme
		this.box.visible = false;
		this.label.visible = false;
	}

	// ikke valgbar ved klik i 3D (en tynd usynlig boks ville give utilsigtet valg)
	raycast(){}
}

// Snit-definition (center, mål, azimut/hældning/rulning) ud fra en BoxVolumes egen transform - bruges både af håndtagene og til at
// konvertere en gammel Clip-boks. Lokal x = længde, y = højde, z = tykkelse (som snitplanBox).
export function snitDefinitionFromBox(box, fallbackAzimuth = 0){
	box.updateMatrix();
	const m = new THREE.Matrix4().makeRotationFromQuaternion(box.quaternion);
	const x = new THREE.Vector3(), y = new THREE.Vector3(), z = new THREE.Vector3();
	m.extractBasis(x, y, z);
	const o = snitplanFromBasis(x.toArray(), y.toArray(), z.toArray(), fallbackAzimuth);
	return Object.assign({
		center: [box.position.x, box.position.y, box.position.z],
		length: Math.abs(box.scale.x),
		height: Math.abs(box.scale.y),
		thickness: Math.abs(box.scale.z),
	}, o);
}
